import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useIntegration from 'api/useIntegration';
import usePermissions from 'api/usePermissions';
import useReceiptsTheme from 'api/useReceiptsTheme';
import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import { AxiosError } from 'axios';
import { EPublisherSolutionModel, ETaskId } from 'common/contracts';
import {
  EPermissionAction,
  EPermissionGroup,
  IPermissions
} from 'common/permissions.types';
import {
  EButtonColor,
  EFeatureFlag,
  ELocalStorageKeys,
  ESectionOfferType
} from 'constants/enums';
import { Builder } from 'design-system/AcSidebar/constants';
import { TabsPanel } from 'design-system/TabPanel/TabPanel';
import { Form, Formik, FormikConfig } from 'formik';
import { useTabTracking } from 'hooks/useTabTracking';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { ERouteContext } from 'routes/types';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { uiActions } from 'store/uiSlice';
import { processRichTextContent } from 'utils/colorUtils';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';

import DialogModal from 'components/Dialog/Dialog';
import { cn } from 'components/lib/utils';
import PageTopBar from 'components/Topbar/PageTopBar';

import { EToasterStatus, showToast } from '../../utils/showToast';

import { CheckoutThemeFormValues } from './CheckoutTheme/types';
import { useGetCheckoutInitialValues } from './CheckoutTheme/useGetCheckoutInitialValues';
import { CompletedThemeFormValues } from './CompletedTheme/types';
import { useGetCompletedInitialValues } from './CompletedTheme/useGetCompletedInitialValues';
import { completedValidationSchema } from './CompletedTheme/validation-schema';
import { GeneralThemeFormValues } from './GeneralSettings/generalTheme.types';
import { useGetGeneralInitialValues } from './GeneralSettings/useGetGeneralInitialValues';
import { generalSchema } from './GeneralSettings/validation-schema';
import { LoginThemeFormValues } from './LoginTheme/types';
import { useGetLoginInitialValues } from './LoginTheme/useGetLoginInitialValues';
import { loginValidationSchema } from './LoginTheme/validation-schema';
import { ReceiptThemeFormValues } from './ReceiptTheme/types';
import { useGetReceiptInitialValues } from './ReceiptTheme/useGetReceiptInitialValues';
import {
  SectionsItem,
  StoreThemeFormValues,
  TabsItem
} from './StoreTheme/storeTheme.types';
import { useGetStoreInitialValues } from './StoreTheme/useGetStoreInitialValues';
import { getStoreSchema } from './StoreTheme/validation-schema';
import { normalizeUrlString } from './utils';

import './style.scss';

export enum EStoreState {
  STORE_SETTINGS = 'store-settings',
  GENERAL = 'general',
  STORE = 'store',
  LOGIN = 'login',
  ACCOUNT_APPROVAL_MODAL = 'account-approval-modal',
  COMPLETED = 'completed',
  CHECKOUT = 'checkout',
  RECEIPT = 'receipt'
}

const SyncUnsavedChanges: React.FC<{ dirty: boolean }> = ({ dirty }) => {
  useUnsavedChanges({ dirty });
  return null;
};

export const ThemeView: React.FC<{ context: ERouteContext }> = ({
  context
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { publisherId } = useParams();
  const { isActionEnabled } = useUserPermissions();
  const { setTaskStatus, hasCategories, isWebStore, isWebCheckout } =
    useChecklistState();
  const { canUserEdit, isSuperAdminByProject, isAdminByProject } =
    usePermissions();

  const initialPath = useMemo(
    () => location.pathname.split('/').filter(Boolean).pop(),
    [location.pathname]
  );

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const { featureFlags, isLoading: isFeatureFlagsLoading } = useFeatureFlags({
    publisherId
  });
  const isStoreSettingsTabEnabled = useMemo(
    () =>
      featureFlags?.[EFeatureFlag.DASHBOARD_STORE_SETTINGS_TAB] ??
      featureFlags?.Dashboard_store_settings_tab,
    [featureFlags]
  );

  const initialTab = useMemo(() => {
    const isValidPathTab = Object.values(EStoreState).includes(
      initialPath as EStoreState
    );
    if (isValidPathTab) {
      const pathTab = initialPath as EStoreState;
      if (
        pathTab === EStoreState.STORE_SETTINGS &&
        !isFeatureFlagsLoading &&
        !isStoreSettingsTabEnabled
      ) {
        return EStoreState.GENERAL;
      }
      return pathTab;
    }
    return isCheckoutUser
      ? EStoreState.CHECKOUT
      : !isFeatureFlagsLoading && isStoreSettingsTabEnabled
        ? EStoreState.STORE_SETTINGS
        : EStoreState.GENERAL;
  }, [
    initialPath,
    isCheckoutUser,
    isStoreSettingsTabEnabled,
    isFeatureFlagsLoading
  ]);

  const [tab, setTab] = useState<EStoreState>(initialTab);
  const [isStoreSettingsDirty, setStoreSettingsDirty] = useState(false);
  const storeSettingsOutletContext = useMemo(() => ({ setStoreSettingsDirty }), []);
  const [displayUnsavedDialog, setDisplayUnsavedDialog] = useState(false);
  const [navigationAction, setNavigationAction] = useState<(() => void) | null>(
    null
  );

  const {
    getGeneralTheme: generalTheme,
    updateGeneralTheme,
    getStoreScreen: storeScreen,
    updateStoreScreen,
    getLoginTheme: loginTheme,
    updateLoginTheme,
    getCheckoutTheme: checkoutTheme,
    updateCheckoutTheme,
    getCompletedTheme: completeTheme,
    updateCompletedTheme
  } = useTheme(publisherId);
  const { getReceiptTheme, upsertReceiptTheme } = useReceiptsTheme(publisherId);

  // getGeneralTheme.refetch();
  const { updateIntegration } = useIntegration(publisherId);
  const { getSections, upsertSections } = useSections(publisherId);
  const { getTabs, upsertTabs } = useTabs(publisherId);
  const dispatch = useDispatch();
  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);

  const { initialValues: generalInitialValues } = useGetGeneralInitialValues();
  const { initialValues: storeInitialValues } = useGetStoreInitialValues();
  const { initialValues: loginInitialValues } = useGetLoginInitialValues();
  const { initialValues: checkoutInitialValues } =
    useGetCheckoutInitialValues();
  const { initialValues: completedInitialValues } =
    useGetCompletedInitialValues();
  const { initialValues: receiptInitialValues } = useGetReceiptInitialValues();

  const enableTabs: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  const tabConfig: Record<EStoreState, FormikConfig<any>> = useMemo(
    () => ({
      [EStoreState.STORE_SETTINGS]: {
        initialValues: {},
        validationSchema: undefined,
        onSubmit: async () => {}
      },
      [EStoreState.GENERAL]: {
        initialValues: generalInitialValues,
        validationSchema: generalSchema,
        onSubmit: async (
          values: GeneralThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          const missingFields: string[] = [];
          if (!values.footer?.privacyPolicyUrl)
            missingFields.push('Privacy Policy URL');
          if (!values.footer?.termsAndConditionsUrl)
            missingFields.push('Terms and Conditions URL');

          if (missingFields.length > 0) {
            showToast({
              message: 'Required fields are empty',
              status: EToasterStatus.ERROR,
              description: `Please fill in: ${missingFields.join(', ')}`
            });
            setSubmitting(false);
            return;
          }

          try {
            const generalThemePayload = {
              ...values,
              maintenanceModeRichText: processRichTextContent(values.maintenanceModeRichText),
            };
            await updateGeneralTheme.mutateAsync(generalThemePayload);
            if (checkoutTheme?.data) {
              await updateCheckoutTheme.mutateAsync({
                ...checkoutTheme.data,
                logo: values.logo
              });
            }
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'General theme updated',
              status: EToasterStatus.SUCCESS
            });

            if (hasCategories && isWebStore) {
              setTaskStatus(ETaskId.CUSTOMIZE_GENERAL_STORE_SETTINGS);
            }

            await generalTheme.refetch();
            await checkoutTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating general theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                )?.message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.STORE]: {
        initialValues: storeInitialValues,
        validationSchema: getStoreSchema(
          !!featureFlags?.[EFeatureFlag.DASHBOARD_STORE_SEO_SETTINGS]
        ),
        onSubmit: async (
          values: StoreThemeFormValues,
          { resetForm, setSubmitting, setFieldValue }
        ) => {
          try {
            const newSectionsData = values.bundleSections
              .concat(values.promotionSections)
              .map((value: SectionsItem) => {
                const { _id, ...rest } = value;
                return rest;
              })
              .filter((item) => !item.toBeDeleted)
              .map((section) => {
                const sectionData: Partial<SectionsItem> = {
                  name: section.name,
                  publisherSectionId: section.publisherSectionId,
                  offerType: section.offerType || ESectionOfferType.BUNDLE
                };
                if (section.image) {
                  sectionData.image = section.image;
                }
                if (section.sectionId) {
                  sectionData.sectionId = section.sectionId;
                }
                return sectionData;
              });

            const storeScreenValues = {
              defaultBanner: values.defaultBanner,
              addToHomeScreen: values.addToHomeScreen,
              backToGameButton: values.backToGameButton,
              header: values.header,
              noOffersTitleText: values.noOffersTitleText,
              noOffersMessageText: values.noOffersMessageText,
              bundleBorderColor: values.bundleBorderColor,
              buttonColor: values.buttonColor,
              buttonTextColor: values.buttonTextColor,
              defaultPlayerImage: values.defaultPlayerImage,
              richText: processRichTextContent(values.richText),
              seo: values.seo
            };

            if (enableTabs) {
              const newTabsData = values.tabs
                .filter((item) => !item.toBeDeleted)
                .map((tab) => {
                  const tabData: Partial<TabsItem> = {
                    name: tab.name,
                    publisherTabId: tab.publisherTabId
                  };
                  if (tab.tabId) {
                    tabData.tabId = tab.tabId;
                  }
                  if (tab.iconUrl) {
                    tabData.iconUrl = tab.iconUrl;
                  }
                  return tabData;
                });

              await upsertTabs.mutateAsync(newTabsData);
            }

            await upsertSections.mutateAsync(newSectionsData);
            await updateStoreScreen.mutateAsync(storeScreenValues);

            dispatch(uiActions.setUnsavedChanges(false));
            await getSections.refetch();
            if (enableTabs) await getTabs.refetch();
            setSubmitting(false);

            showToast({
              message: 'Store page updated',
              status: EToasterStatus.SUCCESS
            });

            if (hasCategories && isWebStore) {
              setTaskStatus(ETaskId.CUSTOMIZE_STORE_PAGE);
            }

            await storeScreen.refetch();
          } catch (error) {
            showToast({
              message: 'Error updating store theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
            if (getSections.data?.result.length > 0) {
              await setFieldValue(
                'bundleSections',
                getSections.data?.result.filter(
                  (section: SectionsItem) =>
                    !section.offerType ||
                    section.offerType === ESectionOfferType.BUNDLE
                )
              );
              await setFieldValue(
                'promotionSections',
                getSections.data?.result.filter(
                  (section: SectionsItem) =>
                    section.offerType === ESectionOfferType.PROMOTION
                )
              );
            }
            if (enableTabs && getTabs.data.length > 0) {
              await setFieldValue('tabs', getTabs.data);
            }
          }
        }
      },
      [EStoreState.LOGIN]: {
        initialValues: loginInitialValues,
        validationSchema: loginValidationSchema,
        onSubmit: async (
          values: LoginThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            await updateLoginTheme.mutateAsync(values);
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Login theme updated',
              status: EToasterStatus.SUCCESS
            });

            if (hasCategories && isWebStore) {
              setTaskStatus(ETaskId.CUSTOMIZE_LOGIN_PAGE);
            }

            await loginTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating login theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.ACCOUNT_APPROVAL_MODAL]: {
        initialValues: loginInitialValues,
        validationSchema: loginValidationSchema,
        onSubmit: async (
          values: LoginThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            await updateLoginTheme.mutateAsync(values);
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Account approval modal updated',
              status: EToasterStatus.SUCCESS
            });

            await loginTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating account approval modal',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.CHECKOUT]: {
        initialValues: checkoutInitialValues,
        onSubmit: async (
          values: CheckoutThemeFormValues,
          { setSubmitting }
        ) => {
          try {
            values.publisherId =
              publisherId ||
              localStorageUtil.get(ELocalStorageKeys.PUBLISHER_ID) ||
              '';
            if (values?.mobileBgImage && values?.mobileBgImage === 'none') {
              values.mobileBgImage = '';
            }

            await updateCheckoutTheme.mutateAsync(values);
            await updateGeneralTheme.mutateAsync({
              logo: values.logo
            });

            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Checkout theme updated',
              status: EToasterStatus.SUCCESS
            });
            setIsCheckoutDialogOpen(true);

            if (hasCategories) {
              const taskId =
                isWebStore || isWebCheckout
                  ? ETaskId.CUSTOMIZE_CHECKOUT
                  : ETaskId.EDIT_CHECKOUT_IN_BUILDER;
              setTaskStatus(taskId);
            }

            await checkoutTheme.refetch();
            await generalTheme.refetch();
          } catch (error) {
            showToast({
              message: 'Error updating checkout theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.COMPLETED]: {
        initialValues: completedInitialValues,
        validationSchema: completedValidationSchema,
        onSubmit: async (
          values: CompletedThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            const integrationValues = {
              deepLinks: [
                {
                  platform: 'ios',
                  deepLink: values.deepLinks.ios
                },
                {
                  platform: 'android',
                  deepLink: values.deepLinks.android
                },
                {
                  platform: 'web',
                  deepLink: values.deepLinks.web
                }
              ]
            };

            await updateCompletedTheme.mutateAsync(values);
            await updateIntegration.mutateAsync(integrationValues);

            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Completed page updated',
              status: EToasterStatus.SUCCESS
            });

            if (hasCategories && isWebStore) {
              setTaskStatus(ETaskId.CUSTOMIZE_SUCCESS_PAGE);
            }

            await completeTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating completed page',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.RECEIPT]: {
        initialValues: receiptInitialValues,
        onSubmit: async (values: ReceiptThemeFormValues, { setSubmitting }) => {
          try {
            values.publisherId =
              publisherId ||
              localStorageUtil.get(ELocalStorageKeys.PUBLISHER_ID) ||
              '';
            const isFooterOn = Boolean(values.addFooter);
            const hasStructuredAddress =
              !!values.companyAddressStructured?.line1 &&
              !!values.companyAddressStructured?.line2 &&
              !!values.companyAddressStructured?.line3;
            if (isFooterOn && !hasStructuredAddress) {
              setSubmitting(false);
              showToast({
                message: 'Please fill all footer address lines',
                status: EToasterStatus.ERROR
              });
              return;
            }

            const getUrl = (url?: string | null, isFooterEnabled?: boolean) =>
              isFooterEnabled ? normalizeUrlString(url) : undefined;

            await upsertReceiptTheme.mutateAsync({
              senderName: values.senderName,
              logo: values.logo,
              headerImage: values.headerImage,
              paymentReceiptTemplateName: values.paymentReceiptTemplateName,
              companyAddressStructured: isFooterOn
                ? values.companyAddressStructured
                : null,
              termsOfServiceUrl: getUrl(values.termsOfServiceUrl, isFooterOn),
              privacyPolicyUrl: getUrl(values.privacyPolicyUrl, isFooterOn),
              supportUrl: getUrl(values.supportUrl, isFooterOn),
              publisherId: values.publisherId
            });
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);
            showToast({
              message: 'Receipt settings updated',
              status: EToasterStatus.SUCCESS
            });
            setIsCheckoutDialogOpen(true);
            if (hasCategories && isWebCheckout) {
              setTaskStatus(ETaskId.CUSTOMIZE_RECEIPT);
            }
            await getReceiptTheme.refetch();
          } catch (error) {
            showToast({
              message: 'Error updating receipt settings',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      }
    }),
    [
      generalInitialValues,
      storeInitialValues,
      loginInitialValues,
      checkoutInitialValues,
      completedInitialValues,
      receiptInitialValues,
      hasCategories,
      generalTheme,
      storeScreen,
      loginTheme,
      publisherId,
      checkoutTheme,
      isWebStore,
      completeTheme,
      isWebCheckout,
      featureFlags
    ]
  );

  const { initialValues, validationSchema, onSubmit }: FormikConfig<any> =
    useMemo(() => tabConfig[tab], [tab, tabConfig]);

  // Add new effect to redirect if path ends with /theme
  useEffect(() => {
    if (location.pathname.endsWith('/theme')) {
      navigate(`${location.pathname}/${tab}`, { replace: true });
    }
  }, [location.pathname, navigate, tab]);

  const shouldAdminTabsBeShown: boolean = useMemo(
    () =>
      Boolean(
        isSuperAdminByProject(publisherId || '') ||
          isAdminByProject(publisherId || '') ||
          permissionsUtil.canAccessBuilder(
            localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) ||
              {},
            publisherId || ''
          )
      ),
    [publisherId, isSuperAdminByProject, isAdminByProject]
  );

  const hasDeeplinkOfferFeatureFlag = useMemo(
    () => featureFlags?.[EFeatureFlag.DASHBOARD_GENERATE_DEEPLINK],
    [featureFlags]
  );

  const hasV2ReceiptsFeatureFlag = useMemo(
    () => featureFlags?.[EFeatureFlag.USE_V2_RECEIPTS_SQS_QUEUE],
    [featureFlags]
  );
  const tabs = useMemo(() => {
    return [
      {
        label: 'Store Settings',
        value: EStoreState.STORE_SETTINGS,
        shouldHide:
          isCheckoutUser ||
          (!isFeatureFlagsLoading && !isStoreSettingsTabEnabled)
      },
      {
        label: 'General Settings',
        value: EStoreState.GENERAL,
        shouldHide:
          isCheckoutUser ||
          (!isFeatureFlagsLoading && !!isStoreSettingsTabEnabled)
      },
      {
        label: 'Store Page',
        value: EStoreState.STORE,
        shouldHide:
          isCheckoutUser ||
          (!isFeatureFlagsLoading && !!isStoreSettingsTabEnabled)
      },
      {
        label: 'Login Page',
        value: EStoreState.LOGIN,
        shouldHide:
          isCheckoutUser || context === ERouteContext.PORTAL_LEVEL_STORE
      },
      {
        label: 'Checkout Page',
        value: EStoreState.CHECKOUT,
        shouldHide: !shouldAdminTabsBeShown
      },
      {
        label: 'Completed Page',
        value: EStoreState.COMPLETED,
        shouldHide: isCheckoutUser
      },
      {
        label: 'Receipt',
        value: EStoreState.RECEIPT,
        shouldHide: !shouldAdminTabsBeShown || !hasV2ReceiptsFeatureFlag
      },
      {
        label: 'Account Approval Modal',
        value: EStoreState.ACCOUNT_APPROVAL_MODAL,
        shouldHide: isCheckoutUser || !hasDeeplinkOfferFeatureFlag
      }
    ];
  }, [
    shouldAdminTabsBeShown,
    isCheckoutUser,
    isStoreSettingsTabEnabled,
    isFeatureFlagsLoading,
    hasDeeplinkOfferFeatureFlag,
    context,
    hasV2ReceiptsFeatureFlag
  ]);

  useEffect(() => {
    if (isCheckoutUser || isFeatureFlagsLoading) return;
    if (!isStoreSettingsTabEnabled && tab === EStoreState.STORE_SETTINGS) {
      setTab(EStoreState.GENERAL);
      navigate(
        `/${context === ERouteContext.PORTAL_LEVEL_STORE ? 'store' : 'project'}/${publisherId}/theme/${EStoreState.GENERAL}`,
        { replace: true }
      );
    }
  }, [
    isCheckoutUser,
    isFeatureFlagsLoading,
    isStoreSettingsTabEnabled,
    tab,
    navigate,
    publisherId,
    context
  ]);

  const { trackTab } = useTabTracking({
    sidebarTab: Builder.title,
    tabs
  });

  const handleTabChange = useCallback(
    (newTab: EStoreState) => {
      if (tab === EStoreState.STORE_SETTINGS && newTab !== EStoreState.STORE_SETTINGS) {
        setStoreSettingsDirty(false);
      }
      trackTab(newTab);
      setTab(newTab);
      const newPath = `/${context === ERouteContext.PORTAL_LEVEL_STORE ? 'store' : 'project'}/${publisherId}/theme/${newTab}`;
      navigate(newPath);
    },
    [context, navigate, publisherId, tab, trackTab]
  );

  const handleNavigationAttempt = useCallback(
    (newTab: EStoreState, dirty: boolean) => {
      if (!dirty) return handleTabChange(newTab);
      setDisplayUnsavedDialog(true);
      setNavigationAction(() => () => handleTabChange(newTab));
    },
    [handleTabChange]
  );

  const builderFormik = (
    <Formik
      key={tab}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ isValid, dirty, isSubmitting, submitForm }) => {
        const hasUnsavedChanges =
          tab === EStoreState.STORE_SETTINGS ? isStoreSettingsDirty : dirty;
        return (
          <Form
            className={cn(
              'storeTheme-form',
              tab === EStoreState.STORE_SETTINGS &&
                'storeTheme-form--store-settings'
            )}
          >
            <SyncUnsavedChanges dirty={hasUnsavedChanges} />
            <TabsPanel
              tabs={tabs}
              outletContext={storeSettingsOutletContext}
              scrollBodyFillsSpace={tab === EStoreState.STORE_SETTINGS}
              headerComponent={
                <PageTopBar
                  withTabsDesign={true}
                  disable={false}
                  headline="Builder"
                  buttons={[
                    {
                      text: 'Save',
                      action: submitForm,
                      disabled:
                        !canUserEdit || !isValid || !dirty || isSubmitting,
                      hidden:
                        tab === EStoreState.STORE_SETTINGS ||
                        !isActionEnabled(
                          null,
                          EPermissionGroup.BUILDER,
                          EPermissionAction.MANAGE,
                          publisherId
                        )
                    }
                  ]}
                />
              }
              handleTabChange={(event: SyntheticEvent, newValue: EStoreState) =>
                handleNavigationAttempt(newValue, hasUnsavedChanges)
              }
              activeTabVal={tab}
            />
            <DialogModal
              headline="Please note"
              isOpen={isCheckoutDialogOpen}
              text="Your changes have been saved. Please allow a few minutes for them to take effect."
              buttons={[
                {
                  text: 'Got it',
                  color: EButtonColor.PRIMARY,
                  variant: 'contained',
                  func: () => setIsCheckoutDialogOpen(false)
                }
              ]}
            />
            <DialogModal
              isOpen={displayUnsavedDialog}
              className="store-settings-unsaved-dialog"
              width="448px"
              headline="Discard changes?"
              text="You have unsaved changes. If you go back now, your changes will not be saved."
              closeButton
              withDivider={false}
              buttons={[
                {
                  text: 'Cancel',
                  color: EButtonColor.SECONDARY,
                  variant: 'outlined',
                  func: () => {
                    setDisplayUnsavedDialog(false);
                  }
                },
                {
                  text: 'Discard changes',
                  color: EButtonColor.PRIMARY,
                  variant: 'contained',
                  style: {
                    backgroundColor: '#16181D',
                    border: '1px solid #16181D',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '8px 16px'
                  },
                  func: () => {
                    setDisplayUnsavedDialog(false);
                    navigationAction?.();
                  }
                }
              ]}
              closeDialog={() => setDisplayUnsavedDialog(false)}
            />
          </Form>
        );
      }}
    </Formik>
  );

  if (tab === EStoreState.STORE_SETTINGS) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {builderFormik}
      </div>
    );
  }

  return builderFormik;
};
